import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
  AIReceptionistKnowledgeBaseCompose,
  type IAcceptedImportProposal,
  ImportProposalAcceptance,
  PracticeSettings,
} from '@principle-theorem/principle-core';
import {
  IAIReceptionistKnowledgeBase,
  type IImportProposal,
  IKnowledgeBaseFieldOverride,
  IKnowledgeBasePushBody,
  IPractice,
  IStaffer,
  KnowledgeBasePushStatus,
  OvieHealthKnowledgeBaseAction,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { switchMap, tap } from 'rxjs/operators';
import { AIReceptionistKnowledgeBaseService } from '../services/ai-receptionist-knowledge-base.service';
import {
  OvieCallableError,
  OvieHealthCallableService,
} from '../services/ovie-health-callable.service';

/**
 * Per-treatment override fields that hold an `IKnowledgeBaseFieldOverride`.
 * Excludes `excludedFromKnowledgeBase`, which is set via `setTemplateExcluded`.
 */
export type KnowledgeBaseOverrideField = 'priceRange' | 'notes';

export interface IAIReceptionistKnowledgeBaseEditorState {
  practice?: WithRef<IPractice>;
  aiKnowledgeBase?: IAIReceptionistKnowledgeBase;
  loading: boolean;
  saving: boolean;
  lastSaveError?: string;
  lastPushStatus?: KnowledgeBasePushStatus;
}

const initialState: IAIReceptionistKnowledgeBaseEditorState = {
  aiKnowledgeBase: undefined,
  loading: true,
  saving: false,
  lastSaveError: undefined,
  lastPushStatus: undefined,
};

@Injectable()
export class AiReceptionistKnowledgeBaseEditorStore extends ComponentStore<IAIReceptionistKnowledgeBaseEditorState> {
  private readonly _ovieHealthCallable = inject(OvieHealthCallableService);

  readonly aiKnowledgeBase$ = this.select((state) => state.aiKnowledgeBase);
  readonly loading$ = this.select((state) => state.loading);
  readonly saving$ = this.select((state) => state.saving);
  readonly lastSaveError$ = this.select((state) => state.lastSaveError);
  readonly lastPushStatus$ = this.select((state) => state.lastPushStatus);
  readonly practice$ = this.select((state) => state.practice);

  readonly setAiKnowledgeBase = this.updater(
    (state, aiKnowledgeBase: IAIReceptionistKnowledgeBase | undefined) => ({
      ...state,
      aiKnowledgeBase,
      loading: false,
    })
  );

  readonly setPractice = this.updater(
    (state, practice: WithRef<IPractice>) => ({
      ...state,
      practice,
    })
  );

  readonly setSaving = this.updater((state, saving: boolean) => ({
    ...state,
    saving,
  }));

  readonly setLastSaveError = this.updater(
    (state, error: string | undefined) => ({
      ...state,
      lastSaveError: error,
    })
  );

  readonly setPushStatus = this.updater(
    (state, status: KnowledgeBasePushStatus | undefined) => ({
      ...state,
      lastPushStatus: status,
    })
  );

  readonly updateSection = this.updater(
    (
      state: IAIReceptionistKnowledgeBaseEditorState,
      update: { section: keyof IAIReceptionistKnowledgeBase; data: unknown }
    ) => ({
      ...state,
      aiKnowledgeBase: state.aiKnowledgeBase
        ? { ...state.aiKnowledgeBase, [update.section]: update.data }
        : state.aiKnowledgeBase,
    })
  );

  /**
   * Applies what the practice accepted from a Website Import to the unsaved
   * Knowledge Base. It never saves or pushes; the editor's existing Save does.
   */
  readonly applyImportProposal = this.updater(
    (
      state: IAIReceptionistKnowledgeBaseEditorState,
      update: { proposal: IImportProposal; accepted: IAcceptedImportProposal }
    ) =>
      state.aiKnowledgeBase
        ? {
            ...state,
            aiKnowledgeBase: ImportProposalAcceptance.merge(
              state.aiKnowledgeBase,
              update.proposal,
              update.accepted,
              () => crypto.randomUUID()
            ),
          }
        : state
  );

  readonly setTemplateOverride = this.updater(
    (
      state: IAIReceptionistKnowledgeBaseEditorState,
      update: {
        templateId: string;
        field: KnowledgeBaseOverrideField;
        value: string;
      }
    ) => {
      if (!state.aiKnowledgeBase) {
        return state;
      }

      const currentTemplateOverrides =
        state.aiKnowledgeBase.templateOverrides ?? {};
      const currentTemplateOverride =
        currentTemplateOverrides[update.templateId] ?? {};

      return {
        ...state,
        aiKnowledgeBase: {
          ...state.aiKnowledgeBase,
          templateOverrides: {
            ...currentTemplateOverrides,
            [update.templateId]: {
              ...currentTemplateOverride,
              [update.field]: {
                overridden: true,
                value: update.value,
              } satisfies IKnowledgeBaseFieldOverride,
            },
          },
        },
      };
    }
  );

  readonly clearTemplateOverride = this.updater(
    (
      state: IAIReceptionistKnowledgeBaseEditorState,
      update: {
        templateId: string;
        field: KnowledgeBaseOverrideField;
      }
    ) => {
      if (!state.aiKnowledgeBase?.templateOverrides?.[update.templateId]) {
        return state;
      }

      const currentTemplateOverrides = state.aiKnowledgeBase.templateOverrides;
      const currentTemplateOverride =
        currentTemplateOverrides[update.templateId];
      const { [update.field]: removedOverride, ...remainingOverrides } =
        currentTemplateOverride;
      void removedOverride;

      const nextTemplateOverrides = { ...currentTemplateOverrides };

      if (Object.keys(remainingOverrides).length === 0) {
        delete nextTemplateOverrides[update.templateId];
      } else {
        nextTemplateOverrides[update.templateId] = remainingOverrides;
      }

      return {
        ...state,
        aiKnowledgeBase: {
          ...state.aiKnowledgeBase,
          templateOverrides: nextTemplateOverrides,
        },
      };
    }
  );

  readonly setTemplateExcluded = this.updater(
    (
      state: IAIReceptionistKnowledgeBaseEditorState,
      update: { templateId: string; excluded: boolean }
    ) => {
      if (!state.aiKnowledgeBase) {
        return state;
      }

      const currentTemplateOverrides =
        state.aiKnowledgeBase.templateOverrides ?? {};
      const currentTemplateOverride =
        currentTemplateOverrides[update.templateId] ?? {};

      if (update.excluded) {
        return {
          ...state,
          aiKnowledgeBase: {
            ...state.aiKnowledgeBase,
            templateOverrides: {
              ...currentTemplateOverrides,
              [update.templateId]: {
                ...currentTemplateOverride,
                excludedFromKnowledgeBase: true,
              },
            },
          },
        };
      }

      const {
        excludedFromKnowledgeBase: removedExclusion,
        ...remainingOverrides
      } = currentTemplateOverride;
      void removedExclusion;

      const nextTemplateOverrides = { ...currentTemplateOverrides };

      if (Object.keys(remainingOverrides).length === 0) {
        delete nextTemplateOverrides[update.templateId];
      } else {
        nextTemplateOverrides[update.templateId] = remainingOverrides;
      }

      return {
        ...state,
        aiKnowledgeBase: {
          ...state.aiKnowledgeBase,
          templateOverrides: nextTemplateOverrides,
        },
      };
    }
  );

  readonly saveAndPush = this.effect<{
    practice: WithRef<IPractice>;
    stafferRef?: DocumentReference<IStaffer>;
  }>((trigger$) =>
    trigger$.pipe(
      tap(() => this.setSaving(true)),
      switchMap(async ({ practice, stafferRef }) => {
        try {
          const currentAiReceptionistKnowledgeBase = this.get().aiKnowledgeBase;
          if (!currentAiReceptionistKnowledgeBase) {
            throw new Error('No AI Receptionist knowledge base data to save');
          }

          // 1. Save AI knowledge base (archive + patch handled internally)
          const savedRef =
            await AIReceptionistKnowledgeBaseService.saveKnowledgeBase(
              practice,
              currentAiReceptionistKnowledgeBase,
              stafferRef
            );
          const savedAiReceptionistKnowledgeBase =
            await Firestore.getDoc(savedRef);

          // 2. Compose
          let composedAiKnowledgeBase: IKnowledgeBasePushBody;
          try {
            composedAiKnowledgeBase =
              await AIReceptionistKnowledgeBaseCompose.composeKnowledgeBase(
                practice,
                savedAiReceptionistKnowledgeBase
              );
          } catch (composeError) {
            const composeMsg =
              composeError instanceof Error
                ? composeError.message
                : 'Compose failed';
            this.setSaving(false);
            this.setLastSaveError(
              `AI knowledge base saved but compose failed: ${composeMsg}`
            );
            return { success: false, saved: true, error: composeMsg };
          }

          // 3. Push via Firebase callable
          // Test mode gets a mock response, so it must not record a push status.
          const recordsPushStatus =
            !PracticeSettings.isAiReceptionistTestModeEnabled(practice);
          try {
            await this._ovieHealthCallable.knowledgeBase({
              action: OvieHealthKnowledgeBaseAction.PutKnowledgeBase,
              practiceRef: practice.ref,
              body: composedAiKnowledgeBase,
            });

            if (recordsPushStatus) {
              this.setPushStatus(KnowledgeBasePushStatus.Success);

              await Firestore.patchDoc(savedRef, {
                lastPushStatus: KnowledgeBasePushStatus.Success,
                lastPushAt: toTimestamp(),
              });
            }
          } catch (pushError) {
            const pushMsg =
              pushError instanceof OvieCallableError
                ? pushError.message
                : pushError instanceof Error
                  ? pushError.message
                  : 'Unknown push error';

            if (recordsPushStatus) {
              this.setPushStatus(KnowledgeBasePushStatus.Failure);

              await Firestore.patchDoc(savedRef, {
                lastPushStatus: KnowledgeBasePushStatus.Failure,
                lastPushAt: toTimestamp(),
              });
            }

            this.setSaving(false);
            this.setLastSaveError(
              `AI knowledge base saved but push failed: ${pushMsg}`
            );
            return { success: false, saved: true, error: pushMsg };
          }

          this.setSaving(false);
          this.setLastSaveError(undefined);
          return { success: true, saved: true };
        } catch (error) {
          const errorMsg =
            error instanceof Error ? error.message : 'Save failed';
          this.setSaving(false);
          this.setLastSaveError(errorMsg);
          return { success: false, saved: false, error: errorMsg };
        }
      })
    )
  );

  constructor() {
    super(initialState);
  }

  getState(): IAIReceptionistKnowledgeBaseEditorState {
    return this.get();
  }
}
